import React from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Avatar,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  IconButton,
  // Paper,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import {
  TrendingUp,
  TrendingDown,
  People,
  AccountBalance,
  Warning,
  CheckCircle,
  MoreVert,
} from '@mui/icons-material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  // BarChart,
  // Bar,
} from 'recharts';

// Mock data for demonstration
const loanVolumeData = [
  { month: 'Jan', amount: 1200000, count: 45 },
  { month: 'Feb', amount: 1850000, count: 62 },
  { month: 'Mar', amount: 2100000, count: 78 },
  { month: 'Apr', amount: 1950000, count: 71 },
  { month: 'May', amount: 2400000, count: 89 },
  { month: 'Jun', amount: 2650000, count: 95 },
];

const loanStatusData = [
  { name: 'Current', value: 68, color: '#4CAF50' },
  { name: 'Past Due', value: 15, color: '#FF9800' },
  { name: 'Under Review', value: 12, color: '#2196F3' },
  { name: 'Default', value: 5, color: '#F44336' },
];

const recentLoans = [
  {
    id: 1,
    client: 'ABC Manufacturing LLC',
    amount: 250000,
    status: 'Approved',
    date: '2024-01-15',
    loanOfficer: 'Sarah Johnson',
  },
  {
    id: 2,
    client: 'Downtown Restaurant Group',
    amount: 125000,
    status: 'Under Review',
    date: '2024-01-14',
    loanOfficer: 'Mike Chen',
  },
  {
    id: 3,
    client: 'Tech Solutions Inc',
    amount: 500000,
    status: 'Funded',
    date: '2024-01-13',
    loanOfficer: 'Lisa Rodriguez',
  },
  {
    id: 4,
    client: 'Green Energy Co',
    amount: 75000,
    status: 'Past Due',
    date: '2024-01-12',
    loanOfficer: 'David Kim',
  },
];

const alerts = [
  {
    id: 1,
    type: 'warning',
    title: 'Payment Overdue',
    description: 'ABC Manufacturing LLC payment is 15 days overdue',
    time: '2 hours ago',
  },
  {
    id: 2,
    type: 'info',
    title: 'New Application',
    description: 'New loan application from Metro Builders requires review',
    time: '4 hours ago',
  },
  {
    id: 3,
    type: 'success',
    title: 'Payment Received',
    description: 'Downtown Restaurant Group made payment of $12,500',
    time: '6 hours ago',
  },
];

interface MetricCardProps {
  title: string;
  value: string;
  change: string;
  changeType: 'positive' | 'negative';
  icon: React.ReactNode;
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, change, changeType, icon }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
            {value}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {changeType === 'positive' ? (
              <TrendingUp sx={{ color: 'success.main', mr: 0.5 }} />
            ) : (
              <TrendingDown sx={{ color: 'error.main', mr: 0.5 }} />
            )}
            <Typography
              variant="body2"
              sx={{
                color: changeType === 'positive' ? 'success.main' : 'error.main',
                fontWeight: 'medium',
              }}
            >
              {change}
            </Typography>
          </Box>
        </Box>
        <Avatar sx={{ bgcolor: 'primary.main' }}>{icon}</Avatar>
      </Box>
    </CardContent>
  </Card>
);

const getStatusColor = (status: string): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  switch (status) {
    case 'Approved':
    case 'Funded':
      return 'success';
    case 'Under Review':
      return 'info';
    case 'Past Due':
      return 'warning';
    case 'Default':
      return 'error';
    default:
      return 'default';
  }
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  
  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold' }}>
        Welcome, {user?.firstName || 'User'}
      </Typography>
      <Typography variant="body1" sx={{ mb: 4, color: 'text.secondary' }}>
        Here's what's happening with your business today.
      </Typography>

      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Total Portfolio"
            value="$24.5M"
            change="+12.5% from last month"
            changeType="positive"
            icon={<AccountBalance />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Active Loans"
            value="284"
            change="+8.2% from last month"
            changeType="positive"
            icon={<TrendingUp />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Active Clients"
            value="156"
            change="+5.1% from last month"
            changeType="positive"
            icon={<People />}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <MetricCard
            title="Default Rate"
            value="2.1%"
            change="-0.3% from last month"
            changeType="positive"
            icon={<Warning />}
          />
        </Grid>
      </Grid>

      {/* Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Loan Volume Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={loanVolumeData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'amount' ? `$${(value as number).toLocaleString()}` : value,
                      name === 'amount' ? 'Amount' : 'Count'
                    ]}
                  />
                  <Area 
                    type="monotone" 
                    dataKey="amount" 
                    stroke="#1B365D" 
                    fill="#1B365D" 
                    fillOpacity={0.6} 
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Loan Status Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={loanStatusData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {loanStatusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`${value}%`, 'Percentage']} />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                {loanStatusData.map((item) => (
                  <Box
                    key={item.name}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 12,
                          height: 12,
                          bgcolor: item.color,
                          borderRadius: '50%',
                          mr: 1,
                        }}
                      />
                      <Typography variant="body2">{item.name}</Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {item.value}%
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Recent Activity and Alerts */}
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  Recent Loans
                </Typography>
                <IconButton>
                  <MoreVert />
                </IconButton>
              </Box>
              <List>
                {recentLoans.map((loan, index) => (
                  <ListItem
                    key={loan.id}
                    divider={index < recentLoans.length - 1}
                    sx={{ px: 0 }}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: 'primary.main' }}>
                        {loan.client.charAt(0)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="subtitle1" sx={{ fontWeight: 'medium' }}>
                            {loan.client}
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            ${loan.amount.toLocaleString()}
                          </Typography>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                          <Typography variant="body2" color="textSecondary">
                            Officer: {loan.loanOfficer} • {loan.date}
                          </Typography>
                          <Chip
                            label={loan.status}
                            color={getStatusColor(loan.status)}
                            size="small"
                          />
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Recent Alerts
              </Typography>
              <List>
                {alerts.map((alert, index) => (
                  <ListItem key={alert.id} divider={index < alerts.length - 1} sx={{ px: 0 }}>
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor:
                            alert.type === 'warning'
                              ? 'warning.main'
                              : alert.type === 'success'
                              ? 'success.main'
                              : 'info.main',
                        }}
                      >
                        {alert.type === 'warning' && <Warning />}
                        {alert.type === 'success' && <CheckCircle />}
                        {alert.type === 'info' && <TrendingUp />}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={alert.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="textSecondary">
                            {alert.description}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {alert.time}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;